import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Button, Dialog, InfoBox, Input, InputPassword, Label } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { useUpdatePaymentGateway } from "~/repository/usePaymentGateways";

export const StripeFormDialog = ({ integration, open, onClose }) => {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState([]);

  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const { mutate: updateStripe } = useUpdatePaymentGateway();

  const validationSchema = yup.object().shape({
    secret: yup.string().trim().required(),
    public_key: yup.string().trim().required(),
  });

  const handleSubmit = useCallback(
    ({ public_key, secret }) => {
      if (integration) {
        updateStripe(
          {
            credit_card: {
              type: "StripeGateway",
              is_enabled: true,
              api_key: secret,
              public_key,
            },
          },
          {
            onSuccess: onClose,
          }
        );
      }
    },
    [integration, onClose, updateStripe]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <Formik
        initialValues={{
          secret: "",
          public_key: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ isSubmitting, errors, setFieldValue, handleSubmit }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();

            handleSubmit();
            if (Object.keys(errors)?.length) {
              setShowErrors(Object.keys(errors));
              return focusFirstInvalidField(errors, false, ["secret", "public_key"]);
            }
          };
          return (
            <Form
              role="form"
              onSubmit={handleFormSubmit}
              autoComplete="off"
              data-testid="stripe-form-dialog"
            >
              <Dialog.Title>{t("Configure Stripe")}</Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content className="tw-flex tw-flex-col tw-gap-4">
                <InfoBox
                  type="neutral"
                  showIcon
                >
                  <Body2>
                    {t("You can find this information by logging in to your Stripe account.")}{" "}
                    <a
                      href="https://activecollab.com/help/books/invoicing/online-payments#how-to-connect-to-stripe"
                      target="_blank"
                      className="tw-no-underline"
                      rel="noreferrer"
                    >
                      {t("Learn More.")}
                    </a>
                  </Body2>
                </InfoBox>
                <Field name="secret">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full tw-max-w-[360px]">
                      <Label
                        size="small"
                        htmlFor="secret"
                      >
                        {t("Secret API Key")}*
                      </Label>
                      <InputPassword
                        {...field}
                        autoComplete="new-password"
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        wrapperClassName="tw-w-full tw-max-w-full"
                        autoFocus
                        className="tw-w-full"
                        id="secret"
                        name="secret"
                        onChange={(value) => setFieldValue("secret", value)}
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Field name="public_key">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <Label
                        size="small"
                        htmlFor="public_key"
                      >
                        {t("Publishable API key")}*
                      </Label>
                      <Input
                        {...field}
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        data-testid="public_key"
                        className="tw-w-full tw-max-w-[360px]"
                        type="text"
                        id="public_key"
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                    </div>
                  )}
                </Field>
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  type="submit"
                  className="tw-mr-3"
                  disabled={isSubmitting}
                >
                  {t("Connect")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

StripeFormDialog.propTypes = {
  integration: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
